// Sugalarm — mobile Settings index hero card (cycle 161).
//
// The glance card at the top of the MOBILE Settings index: avatar (+
// edit-pencil badge) and name for the active member, the live current
// glucose + trend arrow + age, a faint dashed sparkline of the recent
// readings, and the provider-connection line. Composed ENTIRELY from
// data the app already holds — the cycle-12 multiplexed socket reading
// + recent buffer, the cycle-105 `MemberAvatar`, and the same
// `cgmStatus` the index's connection status line reads. No fetches, no
// endpoints, no new wire surface. Mobile-index-only; kiosk + web
// indexes never mount it.

import type { CurrentGlucoseView } from "../../lib/currentGlucose";
import type { Reading } from "../../lib/types";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import { TrendArrow } from "../../components/glucose/TrendArrow";
import { displayValue, fmtAge, trendInfo } from "../../lib/glucose";
import { sparkGeometry } from "../../lib/sparkline";
import styles from "./SettingsHeroCard.module.css";

interface Props {
  /** ⚠ Cycle 435: the card paints a number ONLY from `view.displayValue`,
   *  the selector's released current value. Until this cycle this card
   *  had no freshness gate at all — it painted `socket.lastReading` as it
   *  stood. A raw `Reading` cannot be passed here. */
  view: CurrentGlucoseView;
  /** History, for the sparkline only — never the number. */
  recentReadings: Reading[];
  units: "mg/dL" | "mmol/L";
  memberName: string;
  avatarId: string | null;
  provider: string | null;
  connected: boolean;
  /** Edit-pencil tap → the existing cycle 105–108 Members avatar flow
   *  (the caller routes to the Members pane; no new picker here). */
  onEditAvatar: () => void;
  /** Cycle 308: the translucent-glass + iridescent-border fill (mobile
   *  variant only). Default false keeps the narrow-web drill-down's
   *  solid cycle-161 gradient byte-identical. */
  iri?: boolean;
}

function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function PencilGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M2 10l.5-2.5L8.8 1.2a1 1 0 0 1 1.4 0l.6.6a1 1 0 0 1 0 1.4L4.5 9.5 2 10Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SettingsHeroCard({
  view,
  recentReadings,
  units,
  memberName,
  avatarId,
  provider,
  connected,
  onEditAvatar,
  iri = false,
}: Props) {
  const current = view.displayValue;
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  // The age of the reading the selector judged — shown under a withheld
  // number too, so the card still says how old the last value is.
  const ageMin = view.reading != null ? view.ageMinutes : null;
  const spark = sparkGeometry(recentReadings);

  return (
    <div className={`${styles.card} ${iri ? styles.cardIri : ""}`}>
      <div className={styles.topRow}>
        <div className={styles.avatarWrap}>
          <MemberAvatar
            avatarId={avatarId}
            name={memberName}
            size={56}
            shape="circle"
          />
          <button
            type="button"
            className={styles.editBadge}
            aria-label="Edit member photo"
            onClick={onEditAvatar}
          >
            <PencilGlyph />
          </button>
        </div>
        <div className={styles.identity}>
          <div className={styles.name}>{memberName}</div>
          <div className={styles.valueRow}>
            <span className={`${styles.value} tnum`}>
              {current ? displayValue(current.sgv, units) : "—"}
            </span>
            {current ? (
              <TrendArrow glyph={trend.glyph} zone="in_range" size={26} />
            ) : null}
          </div>
          <div className={styles.age}>
            {ageMin != null ? fmtAge(ageMin) : "No recent reading"}
          </div>
        </div>
        {spark ? (
          <svg
            className={styles.spark}
            viewBox="0 0 120 44"
            aria-hidden
            preserveAspectRatio="none"
          >
            <path className={styles.sparkPath} d={spark.d} />
            <circle
              className={styles.sparkDot}
              cx={spark.cx}
              cy={spark.cy}
              r="3"
            />
          </svg>
        ) : null}
      </div>
      <div className={styles.connLine}>
        {connected ? (
          <>
            <span className={styles.connDot} aria-hidden />
            {capitalize(provider ?? "CGM")} Connected
          </>
        ) : (
          "Not connected"
        )}
      </div>
    </div>
  );
}
